import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Flex,
    HStack,
    Input,
    Modal as ChakraModal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';
import moment from 'moment';
import { Icon, checkboxMenuTriggerMetrics } from '../../ui';
import { AskCarlThread } from '../../types/askCarl';
import { shortThreadAge } from './CarlSidebar';

/**
 * Search-chats palette (ENG-2169) — a scoped-down mirror of the Cmd/Ctrl+K
 * command palette shell (CommandSearchModal): floats near the top of the
 * page over a transparent overlay, bordered card, unstyled input row, and
 * a scrollable list inside. With no query it reads as pure history; typing
 * filters the same recency groups, so search and history share one surface.
 */

const BUCKET_ORDER = ['Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days', 'Older'] as const;

const bucketFor = (timestamp: string): (typeof BUCKET_ORDER)[number] => {
    const when = moment(timestamp);
    const now = moment();
    if (when.isSame(now, 'day')) return 'Today';
    if (when.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
    if (when.isAfter(now.clone().subtract(7, 'days'))) return 'Previous 7 Days';
    if (when.isAfter(now.clone().subtract(30, 'days'))) return 'Previous 30 Days';
    return 'Older';
};

/** Menu-scale type shared with the sidebar rows (13px). */
const ROW_FONT_SIZE = checkboxMenuTriggerMetrics.fontSize;

interface CarlSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    threads: AskCarlThread[];
    activeThreadId: string | null;
    onSelectThread: (threadId: string) => void;
    onNewChat: () => void;
}

const CarlSearchModal = ({
    isOpen,
    onClose,
    threads,
    activeThreadId,
    onSelectThread,
    onNewChat,
}: CarlSearchModalProps) => {
    const [query, setQuery] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    // A fresh open starts from a clean query.
    useEffect(() => {
        if (isOpen) setQuery('');
    }, [isOpen]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const rows = q ? threads.filter((thread) => thread.title.toLowerCase().includes(q)) : threads;
        return [...rows].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
    }, [threads, query]);

    const buckets = useMemo(() => {
        const map = new Map<string, AskCarlThread[]>();
        for (const thread of filtered) {
            const bucket = bucketFor(thread.updatedAt);
            if (!map.has(bucket)) map.set(bucket, []);
            map.get(bucket)!.push(thread);
        }
        return map;
    }, [filtered]);

    const openThread = (threadId: string) => {
        onSelectThread(threadId);
        onClose();
    };

    return (
        <ChakraModal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            initialFocusRef={searchRef}
            scrollBehavior="inside"
        >
            {/* Mirrors the command palette: no dim — the panel floats above the page. */}
            <ModalOverlay bg="transparent" />
            <ModalContent
                mt="14vh"
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="xl"
                overflow="hidden"
                bg="white"
                boxShadow="none"
            >
                {/* Input row */}
                <HStack
                    px={3.5}
                    py={2.5}
                    spacing={2.5}
                    borderBottomWidth="1px"
                    borderBottomStyle="solid"
                    borderColor="border.subtle"
                >
                    <Icon name="search" size="sm" color="fg.muted" />
                    <Input
                        ref={searchRef}
                        variant="unstyled"
                        fontSize="sm"
                        color="fg.default"
                        placeholder="Search chats…"
                        // The field itself carries zero chrome — no border, no radius,
                        // no ring — and the caret gets inner padding so its blink never
                        // touches a curved edge (mirrors CommandSearchModal's input).
                        px={3}
                        py={1.5}
                        border="0"
                        borderRadius="none"
                        outline="none"
                        bg="transparent"
                        boxShadow="none"
                        _focus={{ boxShadow: 'none', outline: 'none' }}
                        _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                        _placeholder={{ color: 'fg.muted' }}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && filtered.length > 0) {
                                event.preventDefault();
                                openThread(filtered[0].threadId);
                            }
                        }}
                    />
                </HStack>

                <ModalBody p={2} maxH="46vh" overflowY="auto">
                    {/* New chat shortcut */}
                    <Flex
                        as="button"
                        type="button"
                        w="100%"
                        align="center"
                        gap={2}
                        px={2}
                        py={1.5}
                        borderRadius="md"
                        cursor="pointer"
                        transition="background 0.12s"
                        _hover={{ bg: 'bg.subtle' }}
                        onClick={() => {
                            onNewChat();
                            onClose();
                        }}
                    >
                        <Icon name="edit" size="sm" color="fg.muted" />
                        <Text fontSize={ROW_FONT_SIZE} fontWeight="medium" color="fg.default">
                            New chat
                        </Text>
                    </Flex>

                    {/* Grouped history */}
                    {filtered.length === 0 ? (
                        <Text fontSize={ROW_FONT_SIZE} color="fg.muted" textAlign="center" py={6}>
                            {threads.length === 0 ? 'No conversations yet.' : 'No chats match your search.'}
                        </Text>
                    ) : (
                        <VStack spacing={2.5} align="stretch" mt={2}>
                            {BUCKET_ORDER.filter((bucket) => buckets.has(bucket)).map((bucket) => (
                                <Box key={bucket}>
                                    <Text
                                        px={2}
                                        mb={0.5}
                                        fontSize="xs"
                                        color="fg.muted"
                                        fontWeight="semibold"
                                        letterSpacing="0.06em"
                                        textTransform="uppercase"
                                    >
                                        {bucket}
                                    </Text>
                                    <VStack spacing={0} align="stretch">
                                        {buckets.get(bucket)!.map((thread) => (
                                            <Flex
                                                key={thread.threadId}
                                                as="button"
                                                type="button"
                                                align="center"
                                                gap={2}
                                                px={2}
                                                py={1.5}
                                                borderRadius="md"
                                                cursor="pointer"
                                                textAlign="left"
                                                bg={thread.threadId === activeThreadId ? 'brand.50' : 'transparent'}
                                                _hover={{ bg: thread.threadId === activeThreadId ? 'brand.50' : 'bg.subtle' }}
                                                transition="background 0.12s"
                                                onClick={() => openThread(thread.threadId)}
                                            >
                                                <Text
                                                    flex={1}
                                                    minW={0}
                                                    fontSize={ROW_FONT_SIZE}
                                                    color="fg.default"
                                                    fontWeight={thread.threadId === activeThreadId ? 'semibold' : 'normal'}
                                                    whiteSpace="nowrap"
                                                    overflow="hidden"
                                                    textOverflow="ellipsis"
                                                >
                                                    {thread.title}
                                                </Text>
                                                <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                                                    {shortThreadAge(thread.updatedAt)}
                                                </Text>
                                            </Flex>
                                        ))}
                                    </VStack>
                                </Box>
                            ))}
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </ChakraModal>
    );
};

export default CarlSearchModal;
